import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { sendFirebasePasswordReset } from '@/lib/firebasePasswordReset';
import { API_BASE_URL as API } from '@/lib/apiBase';

const CollaboratorAuthContext = createContext(null);

export const CollaboratorAuthProvider = ({ children }) => {
  const [collaborator, setCollaborator] = useState(null);
  const [loading, setLoading] = useState(true);

  const refreshCollaborator = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/collaborator-auth/me`, { withCredentials: true });
      setCollaborator(response.data);
      return response.data;
    } catch (error) {
      setCollaborator(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshCollaborator();
  }, [refreshCollaborator]);

  const logout = useCallback(async () => {
    try {
      await axios.post(`${API}/collaborator-auth/logout`, {}, { withCredentials: true });
    } finally {
      setCollaborator(null);
      window.dispatchEvent(new CustomEvent('learnAuthChanged', { detail: { authenticated: false } }));
    }
  }, []);

  const updateCollaborator = useCallback(async (payload) => {
    const response = await axios.put(`${API}/collaborator-auth/me`, payload, { withCredentials: true });
    setCollaborator(response.data);
    return response.data;
  }, []);

  const changePassword = useCallback(async ({ currentPassword, newPassword }) => {
    const email = collaborator?.email?.trim();
    if (!email) {
      throw new Error('Collaborator email is missing for password change.');
    }
    try {
      const repairResponse = await axios.post(
        `${API}/collaborator-auth/repair-password-sync`,
        {
          current_password: currentPassword,
          new_password: newPassword,
        },
        { withCredentials: true }
      );

      try {
        await sendFirebasePasswordReset(email);
        return {
          ...repairResponse.data,
          message: 'Password changed successfully. A confirmation email has been sent. Please check your inbox and spam folder.',
        };
      } catch (resetError) {
        return {
          ...repairResponse.data,
          message: 'Password changed successfully. A confirmation email has been sent.',
        };
      }
    } catch (error) {
      throw error;
    }
  }, [collaborator?.email]);

  const value = useMemo(
    () => ({
      collaborator,
      loading,
      hasSession: Boolean(collaborator),
      isAuthenticated: Boolean(collaborator),
      setCollaborator,
      refreshCollaborator,
      logout,
      updateCollaborator,
      changePassword,
    }),
    [collaborator, loading, logout, refreshCollaborator, updateCollaborator, changePassword]
  );

  return <CollaboratorAuthContext.Provider value={value}>{children}</CollaboratorAuthContext.Provider>;
};

export const useCollaboratorAuth = () => {
  const context = useContext(CollaboratorAuthContext);
  if (!context) {
    throw new Error('useCollaboratorAuth must be used within a CollaboratorAuthProvider');
  }
  return context;
};
